import { ImageIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ImageModerationShape,
  imageModerationType,
  comparisonOperators,
  ComparisonOperator,
  ImageModerationProps,
  imageModerationCategories,
  ImageModerationCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isImageModerationShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ImageModerationShape {
  return shape.type === imageModerationType;
}

export class ImageModerationTool extends ToolStateNode {
  static override id = imageModerationType;

  static override uiToolDef = (editor: Editor) => ({
    id: imageModerationType,
    icon: ImageIcon,
    label: ImageModerationShapeUtil.docs.title,
    defaultColor: ImageModerationShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(imageModerationType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ImageModeration}
        util={ImageModerationShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: imageModerationType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ImageModerationShapeUtil extends BindableUtil<ImageModerationShape> {
  static override migrations = createMigrations(imageModerationType);
  static override type = imageModerationType;

  static override docs = {
    defaultColor: "pink" as const,
    title: "Image Moderation",
    description:
      "Use AI to analyze images for NSFW content. This helps filter posts based on image safety ratings.",
  };

  override getType() {
    return imageModerationType;
  }

  static override props: RecordProps<ImageModerationShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    imageModerationCategory: T.literalEnum(...imageModerationCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      imageModerationCategory: "SFW" as const,
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: ImageModerationShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ImageModeration
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ImageModeration({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ImageModerationShape;
  className?: string;
  updateShape: (
    shape: ImageModerationShape,
    props: Partial<ImageModerationProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          ImageModerationShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={ImageModerationShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Image Moderation Category"
            options={imageModerationCategories}
            value={shape.props.imageModerationCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                imageModerationCategory: v as ImageModerationCategory,
              })
            }
            tooltip={ImageModerationShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
